import { z } from 'zod';
import { ConversationId, ConversationStatus } from './conversations.js';
import { ReplyTemplateId } from './templates.js';
import { ScheduleFor } from './scheduled-email.js';
/**
 * Bulk conversation actions. A single endpoint
 * (`POST /api/conversations/bulk-action`) fans ONE action out over many
 * conversations, reusing the exact same per-conversation services as the single
 * routes (status update / manual pipeline run / reply send / scheduled email).
 * Best-effort: one conversation failing never aborts the rest — the response
 * carries a per-conversation result list plus a summary.
 */
/** Cap on conversations per bulk request — bounds the server-side loop. */
export const BULK_ACTION_MAX = 200;
export const BulkConversationAction = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('change_status'),
        status: ConversationStatus,
        /** Same optional operator note as the single-conversation `UpdateConversationRequest.comment`. */
        comment: z.string().trim().min(1).max(4000).optional(),
    }),
    z.object({ type: z.literal('run_pipeline'), pipelineKey: z.string().min(1).max(200) }),
    z.object({ type: z.literal('send_reply'), templateId: ReplyTemplateId }),
    z.object({
        type: z.literal('schedule_email'),
        templateId: ReplyTemplateId,
        scheduleFor: ScheduleFor,
    }),
]);
/**
 * Conversation statuses a BATCH pipeline run may touch. Running a pipeline over
 * a finished conversation mails a customer whose case is closed — the most
 * expensive miss available, so the batch path refuses it server-side. The
 * SINGLE-conversation route (`POST /:id/pipelines/:key/run`) is deliberately NOT
 * restricted: one operator acting on one thread they are looking at is a
 * different risk class from one click fanning out over 200.
 */
export const BULK_PIPELINE_RUNNABLE_STATUSES = ['open', 'snoozed', 'waiting'];
export const BulkConversationActionRequest = z
    .object({
    conversationIds: z.array(ConversationId).min(1).max(BULK_ACTION_MAX),
    action: BulkConversationAction,
    /**
     * Preview only: run every eligibility check and return the same
     * `{results, summary}`, but reserve nothing, enqueue nothing and send
     * nothing. Same endpoint (not a separate one) so the preview and the real
     * run can never drift apart — they are literally the same code path with an
     * early return before the first write.
     */
    dryRun: z.boolean().optional(),
    /**
     * Opt back INTO conversations this pipeline already ran through
     * successfully. Default (absent/false) SKIPS them with reason
     * `pipeline_already_ran` — the guard against re-mailing a customer who
     * already received this pipeline's email.
     */
    includeAlreadyRan: z.boolean().optional(),
})
    // Both flags are `run_pipeline`-only. Accepting-and-ignoring them on the other
    // actions is the dangerous reading: an operator (or a script) would believe
    // `dryRun: true` made a bulk `send_reply` harmless while 200 emails went out.
    // Fail closed with a 400 instead.
    .superRefine((v, ctx) => {
    if (v.action.type === 'run_pipeline')
        return;
    for (const flag of ['dryRun', 'includeAlreadyRan']) {
        if (v[flag] !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [flag],
                message: `${flag} is only supported for action.type='run_pipeline'`,
            });
        }
    }
});
export const BulkConversationActionItemResult = z.object({
    conversationId: ConversationId,
    ok: z.boolean(),
    /** True when the conversation was deliberately not acted on (e.g. pipeline not eligible). */
    skipped: z.boolean().optional(),
    /**
     * Machine-readable code for a skip or failure (e.g. `pipeline_not_eligible`,
     * `pipeline_already_ran`, `status_not_runnable`, `no_recipient`).
     */
    reason: z.string().optional(),
});
export const BulkConversationActionResponse = z.object({
    results: z.array(BulkConversationActionItemResult),
    summary: z.object({
        ok: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    }),
    /**
     * Echo of the request's `dryRun`, present ONLY on a preview. Load-bearing, not
     * decorative: Zod strips unknown keys, so an admin-api that predates the flag
     * would answer a `dryRun: true` request by REALLY running the batch. The
     * client therefore treats a missing echo as "this server does not know about
     * previews" and reports it loudly instead of showing the response as a count.
     */
    dryRun: z.boolean().optional(),
});
//# sourceMappingURL=conversations-bulk.js.map